// Envelopes: the unit of the journal (docs/phase-2-notes.md § Le journal).
//
// header = "env-v1\n" space \n author \n seq \n prev \n epoch \n nonce
// ct     = AES-256-GCM(content key of `epoch`, JSON(payload)), AAD = header
// sig    = Ed25519(author's device key, header \n ct)
// Each author's envelopes form a hash chain (prev = hash of seq-1): a relay can neither drop one
// silently in the middle nor fork an author's history.

import { b64url, fromB64url, randomBytes } from '../core/ids';
import { contentKey, sign, verify, type DeviceRecord, type LocalDevice, type SpaceSecrets } from './keys';

export type Envelope = {
  v: 1;
  space: string;
  author: string;
  seq: number;
  prev: string | null;
  epoch: number;
  nonce: string;
  ct: string;
  sig: string;
};

/** Every payload carries the author's clock (ms), used for history and last-writer-wins metadata. */
export type Payload =
  | { type: 'note.update'; ts: number; note: string; update: string }
  | { type: 'folder.set'; ts: number; folder: string; name: string; color: string | null; position: number; deleted?: boolean }
  | { type: 'device.add'; ts: number; device: DeviceRecord }
  | { type: 'device.revoke'; ts: number; device: string; afterSeq: number }
  | { type: 'space.rekey'; ts: number; epoch: number; sealed: Record<string, { nonce: string; ct: string }> }
  // hands an epoch's secret (of the rotation by `author`) to devices that did not get it
  | { type: 'space.share'; ts: number; epoch: number; author: string; sealed: Record<string, { nonce: string; ct: string }> }
  // my space: a conversation this person belongs to (its secrets reach my other devices this way)
  | { type: 'conv.add'; ts: number; conv: string; secrets: SpaceSecrets; peer?: { identity: string; name: string }; kind?: 'direct' | 'group'; name?: string }
  | { type: 'conv.leave'; ts: number; conv: string }
  // groups (phase 4)
  | { type: 'group.meta'; ts: number; name: string }
  | { type: 'group.invite'; ts: number; group: string; secrets: SpaceSecrets; name: string }
  | { type: 'member.leave'; ts: number; person: string }
  | { type: 'member.remove'; ts: number; person: string }
  // conversations (phase 3)
  | { type: 'msg.post'; ts: number; id: string; text: string; replyTo?: string }
  | { type: 'msg.edit'; ts: number; id: string; text: string }
  | { type: 'msg.delete'; ts: number; id: string }
  | { type: 'msg.react'; ts: number; id: string; emoji: string; on: boolean }
  | { type: 'msg.read'; ts: number; upTo: number }
  // my display name: in my space (shared by my devices) and in each conversation (seen by contacts)
  | { type: 'profile.set'; ts: number; name: string }
  // my private nickname for a contact (my space only)
  | { type: 'contact.alias'; ts: number; person: string; name: string | null };

const enc = new TextEncoder();
const dec = new TextDecoder();

export function header(e: Omit<Envelope, 'ct' | 'sig'>): string {
  return ['env-v1', e.space, e.author, e.seq, e.prev ?? '-', e.epoch, e.nonce].join('\n');
}

export async function sealEnvelope(
  device: LocalDevice,
  base: { space: string; seq: number; prev: string | null; epoch: number },
  secret: string,
  payload: Payload,
): Promise<Envelope> {
  const head = { v: 1 as const, ...base, author: device.id, nonce: b64url(randomBytes(12)) };
  const h = header(head);
  const ct = b64url(
    new Uint8Array(
      await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: fromB64url(head.nonce), additionalData: enc.encode(h) },
        await contentKey(secret),
        enc.encode(JSON.stringify(payload)),
      ),
    ),
  );
  return { ...head, ct, sig: await sign(device, enc.encode(`${h}\n${ct}`)) };
}

export async function openEnvelope(e: Envelope, secret: string): Promise<Payload> {
  const pt = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: fromB64url(e.nonce), additionalData: enc.encode(header(e)) },
    await contentKey(secret),
    fromB64url(e.ct),
  );
  return JSON.parse(dec.decode(pt)) as Payload;
}

export function verifyEnvelope(e: Envelope, signPub: string): Promise<boolean> {
  return verify(signPub, e.sig, enc.encode(`${header(e)}\n${e.ct}`));
}

export async function hashEnvelope(e: Envelope): Promise<string> {
  const bytes = enc.encode(`${header(e)}\n${e.ct}\n${e.sig}`);
  return b64url(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))).slice(0, 32);
}

/** Shape check for envelopes coming from the network, before any crypto. */
export function isEnvelope(raw: unknown): raw is Envelope {
  if (!raw || typeof raw !== 'object') return false;
  const e = raw as Record<string, unknown>;
  const str = (k: string, max = 512) => typeof e[k] === 'string' && (e[k] as string).length <= max;
  return (
    e.v === 1 &&
    str('space', 64) &&
    str('author', 64) &&
    Number.isSafeInteger(e.seq) &&
    (e.seq as number) >= 1 &&
    (e.prev === null || str('prev', 64)) &&
    Number.isSafeInteger(e.epoch) &&
    (e.epoch as number) >= 0 &&
    str('nonce', 32) &&
    typeof e.ct === 'string' &&
    (e.ct as string).length <= 4 * 1024 * 1024 &&
    str('sig', 128)
  );
}
